import type { BoardLayer, Keyboard } from "@/domain/types";
import { importKLE, type KLEData } from "./kle-import";
import {
  importQMK,
  importVIA,
  type QmkInfo,
  type QmkKeymap,
  type ViaDefinition,
  type ViaKeymap,
} from "./via-qmk-import";

/** One half of a two-file board upload: VIA definition + keymap, or QMK info + keymap. */
export type BoardPartFormat = "via-definition" | "via-keymap" | "qmk-info" | "qmk-keymap";

/** A single KLE keyboard, parsed but not yet imported, as the detect stage hands it over. */
export interface KeyboardContent {
  format: "kle";
  rows: KLEData;
  name: string | null;
}

const PART_LABEL: Record<BoardPartFormat, string> = {
  "via-definition": "VIA definition",
  "via-keymap": "VIA keymap",
  "qmk-info": "QMK info.json",
  "qmk-keymap": "QMK keymap",
};

const PART_PARTNER: Record<BoardPartFormat, BoardPartFormat> = {
  "via-definition": "via-keymap",
  "via-keymap": "via-definition",
  "qmk-info": "qmk-keymap",
  "qmk-keymap": "qmk-info",
};

export function partLabel(format: BoardPartFormat): string {
  return PART_LABEL[format];
}

/** The label of the file that completes this part. */
export function partnerLabel(format: BoardPartFormat): string {
  return PART_LABEL[PART_PARTNER[format]];
}

export function isPartnerFormat(a: BoardPartFormat, b: BoardPartFormat): boolean {
  return PART_PARTNER[a] === b;
}

/** An imported custom Keyboard with its board layers: KLE carries one, VIA/QMK several. */
export interface ImportedKeyboard {
  keyboard: Keyboard;
  boardLayers: BoardLayer[];
  defaultLayerId: string;
  format: "kle" | "via" | "qmk";
}

/** The board layer an imported Keyboard renders through until the player picks another. */
export function defaultBoardLayer(imported: ImportedKeyboard): BoardLayer {
  const found = imported.boardLayers.find((l) => l.id === imported.defaultLayerId);
  const first = imported.boardLayers[0];
  if (!found && !first) throw new Error("Imported keyboard has no board layers.");
  return found ?? (first as BoardLayer);
}

/** Rename a board layer, the one permitted edit. Returns a new ImportedKeyboard. */
export function renameBoardLayer(
  imported: ImportedKeyboard,
  layerId: string,
  name: string,
): ImportedKeyboard {
  return {
    ...imported,
    boardLayers: imported.boardLayers.map((l) => (l.id === layerId ? { ...l, name } : l)),
  };
}

/** The file name without its extension, or null when empty. */
export function fileStem(fileName?: string): string | null {
  if (!fileName) return null;
  const stem = fileName.replace(/\.[^.]+$/, "").trim();
  return stem || null;
}

/** Single-file entry point: import already-detected KLE content, naming the board. */
export function importKeyboard(
  content: KeyboardContent,
  opts: { id: string; fileName?: string },
): ImportedKeyboard {
  const name = content.name ?? fileStem(opts.fileName) ?? "Imported keyboard";
  const { keyboard, keyboardLayout } = importKLE(content.rows, { id: opts.id, name });
  // KLE carries no board layers; its legend map becomes the single default layer, converted
  // code-keyed to position-keyed. Placeholder codes are unique per key and keep their own
  // legends; keys sharing a real code share that code's char.
  const chars = keyboard.keys.map((k) => keyboardLayout.map[k.code] ?? null);
  const layer: BoardLayer = {
    kind: "kle",
    id: `${opts.id}-legends`,
    name: `${name} legends`,
    chars,
    empty: chars.every((c) => c == null),
  };
  return { keyboard, boardLayers: [layer], defaultLayerId: layer.id, format: "kle" };
}

/** A detected board part plus where it came from, for pairing and naming. */
export interface BoardPartFile {
  format: BoardPartFormat;
  data: unknown;
  name: string | null;
  fileName?: string;
}

/** Two-file entry point: a VIA definition + keymap, or a QMK info.json + keymap, in either order. */
export function importKeyboardPair(
  a: BoardPartFile,
  b: BoardPartFile,
  opts: { id: string },
): ImportedKeyboard {
  if (!isPartnerFormat(a.format, b.format)) {
    throw new Error(
      `A ${partLabel(a.format)} pairs with a ${partnerLabel(a.format)}, not a ${partLabel(b.format)}.`,
    );
  }
  const fallback = a.name ?? b.name ?? fileStem(a.fileName) ?? fileStem(b.fileName);
  if (a.format === "via-definition" || a.format === "via-keymap") {
    const def = (a.format === "via-definition" ? a : b).data as ViaDefinition;
    const km = (a.format === "via-keymap" ? a : b).data as ViaKeymap;
    return { ...importVIA(def, km, { id: opts.id, name: fallback }), format: "via" };
  }
  const info = (a.format === "qmk-info" ? a : b).data as QmkInfo;
  const km = (a.format === "qmk-keymap" ? a : b).data as QmkKeymap;
  return { ...importQMK(info, km, { id: opts.id, name: fallback }), format: "qmk" };
}
